import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useContacts } from "@/hooks/useContacts";
import { useOpportunities } from "@/hooks/useOpportunities";
import { usePipelinesSettings } from "@/hooks/usePipelinesSettings";
import { useTags } from "@/hooks/useTags";
import { useTeam } from "@/hooks/useTeam";
import { useLossReasons } from "@/hooks/useLossReasons";
import { useCurrency } from "@/hooks/useCurrency";
import { useAuth } from "@/contexts/AuthContext";
import {
  dateInputToIso,
  isFutureDateInputValue,
  todayDateInputValue,
} from "@/lib/dateInput";
import { deriveOpportunityStatus } from "@/lib/opportunityOutcome";
import { normalizeTagName } from "@/lib/tagName";
import { getContactPhone } from "@/utils/contactUtils";
import {
  COUNTRY_CALLING_CODES,
  DEFAULT_COUNTRY_CALLING_CODE,
  getCountryCallingCodeLabel,
  joinPhoneWithCountryCode,
} from "@/data/countryCallingCodes";
import { X, Search, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AddOpportunityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPipelineId: string;
  pipelines?: Array<{id: string; name: string; is_active?: boolean}>;
  stages?: Array<{id: string; name: string; pipeline_id: string; is_active?: boolean; stage_type?: 'normal' | 'won' | 'lost'}>;
  onPipelineChange?: (pipelineId: string) => void;
  createOpportunity?: any;
  findExistingOpportunities?: any;
}

const AddOpportunityDialog: React.FC<AddOpportunityDialogProps> = ({
  open,
  onOpenChange,
  selectedPipelineId,
  pipelines: externalPipelines,
  stages: externalStages,
  onPipelineChange,
  createOpportunity: externalCreateOpportunity,
  findExistingOpportunities: externalFindExistingOpportunities
}) => {
  const { t } = useTranslation("crm");
  const { symbol } = useCurrency();
  const { user } = useAuth();
  const { members, loading: loadingMembers } = useTeam();
  const { lossReasons, loading: loadingReasons, createLossReason } = useLossReasons();
  const { contacts, createContact } = useContacts();
  const { createOpportunity: internalCreateOpportunity, findExistingOpportunities: internalFindExistingOpportunities } = useOpportunities(selectedPipelineId);
  const { stages: internalStages, pipelines: internalPipelines } = usePipelinesSettings();
  const { tags: accountTags } = useTags();

  const createOpportunity = externalCreateOpportunity || internalCreateOpportunity;
  const findExistingOpportunities = externalFindExistingOpportunities || internalFindExistingOpportunities;

  const stages = externalStages || internalStages;
  const pipelines = externalPipelines || internalPipelines;

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  // O mesmo contato pode ter vários cards no mesmo funil. Não bloqueamos, apenas
  // avisamos e exigimos uma segunda confirmação antes de criar a duplicata.
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);

  const [formData, setFormData] = useState({
    contactId: '',
    newContactName: '',
    newContactPhone: '',
    newContactCountryCode: DEFAULT_COUNTRY_CALLING_CODE,
    title: '',
    product: '',
    price: '',
    pipelineId: selectedPipelineId,
    stageId: '',
  });

  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [isNewContact, setIsNewContact] = useState(false);
  const [searchContact, setSearchContact] = useState('');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const tagContainerRef = useRef<HTMLDivElement>(null);

  // Criar direto numa etapa de ganho/perda é fechar o card: sem estes campos a
  // oportunidade nasceria dentro da coluna sem nunca ter sido ganha nem perdida.
  const [saleDate, setSaleDate] = useState('');
  const [wonByUserId, setWonByUserId] = useState('');
  const [lossReasonId, setLossReasonId] = useState('');
  const [lostByUserId, setLostByUserId] = useState('');
  const [isAddingReason, setIsAddingReason] = useState(false);
  const [newReasonName, setNewReasonName] = useState('');
  const [isCreatingReason, setIsCreatingReason] = useState(false);

  const pipelineStages = stages.filter(s => s.pipeline_id === (formData.pipelineId || selectedPipelineId) && s.is_active);
  const firstStageId = pipelineStages.length > 0 ? pipelineStages[0].id : '';

  const activeMembers = members.filter((m) => m.is_active);
  const currentMemberId =
    activeMembers.find((member) => member.auth_user_id === user?.auth_user_id)?.id ?? '';

  const derivedStatus = deriveOpportunityStatus(
    pipelineStages.find(s => s.id === (formData.stageId || firstStageId))?.stage_type
  );

  const today = todayDateInputValue();
  const saleDateIsFuture = derivedStatus === 'won' && isFutureDateInputValue(saleDate);
  const missingLossReason = derivedStatus === 'lost' && !lossReasonId;

  const resetOutcomeFields = () => {
    setSaleDate('');
    setWonByUserId('');
    setLossReasonId('');
    setLostByUserId('');
    setIsAddingReason(false);
    setNewReasonName('');
  };

  // Trocar de etapa limpa os campos do desfecho anterior, senão um card criado em
  // "Ganho" depois de passar por "Perdido" levaria junto o motivo da perda
  const applyStage = (newStageId: string) => {
    setFormData(prev => ({ ...prev, stageId: newStageId }));
    const stageType = pipelineStages.find(s => s.id === newStageId)?.stage_type ?? 'normal';

    if (stageType === 'won') {
      setLossReasonId('');
      setLostByUserId('');
      setIsAddingReason(false);
    } else if (stageType === 'lost') {
      setSaleDate('');
      setWonByUserId('');
    } else {
      resetOutcomeFields();
    }
  };

  // Os padrões (hoje / usuário logado) entram aqui e não no clique da etapa porque
  // a equipe carrega de forma assíncrona: escolher "Ganho" antes disso deixaria o
  // responsável vazio para sempre
  useEffect(() => {
    if (!open) return;
    if (derivedStatus === 'won') {
      setSaleDate((current) => current || todayDateInputValue());
      setWonByUserId((current) => current || currentMemberId);
    } else if (derivedStatus === 'lost') {
      setLostByUserId((current) => current || currentMemberId);
    }
  }, [open, derivedStatus, currentMemberId]);

  const handleCreateNewReason = async () => {
    if (!newReasonName.trim()) return;
    setIsCreatingReason(true);
    const newReason = await createLossReason(newReasonName.trim());
    setIsCreatingReason(false);
    if (newReason) {
      setLossReasonId(newReason.id);
      setNewReasonName('');
      setIsAddingReason(false);
    }
  };

  // Update stageId when pipeline changes
  useEffect(() => {
    if (open && formData.pipelineId && pipelineStages.length > 0 && !formData.stageId) {
      setFormData(prev => ({ ...prev, stageId: firstStageId }));
    }
  }, [open, formData.pipelineId, firstStageId, pipelineStages.length]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        contactId: '',
        newContactName: '',
        newContactPhone: '',
        newContactCountryCode: DEFAULT_COUNTRY_CALLING_CODE,
        title: '',
        product: '',
        price: '',
        pipelineId: selectedPipelineId,
        stageId: firstStageId,
      });
      setIsNewContact(false);
      setSelectedContact(null);
      setSearchContact('');
      setTags([]);
      setTagInput('');
      setMessage(null);
      setDuplicateConfirmed(false);
      resetOutcomeFields();
    }
  }, [open, selectedPipelineId, firstStageId]);

  // Trocar de contato ou de funil invalida a confirmação de duplicata, senão o
  // usuário confirmaria às cegas um aviso que não é mais o dele
  useEffect(() => {
    setDuplicateConfirmed(false);
    setMessage(prev => (prev?.type === 'warning' ? null : prev));
  }, [formData.contactId, formData.pipelineId]);

  const filteredContacts = contacts.filter(contact => {
    const searchLower = searchContact.toLowerCase();
    return (
      contact.display_name?.toLowerCase().includes(searchLower) ||
      contact.phone?.toLowerCase().includes(searchLower) ||
      contact.push_name_last?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    if (!open) {
      setFormData({
        contactId: '',
        newContactName: '',
        newContactPhone: '',
        newContactCountryCode: DEFAULT_COUNTRY_CALLING_CODE,
        title: '',
        product: '',
        price: '',
        pipelineId: selectedPipelineId,
        stageId: '',
      });
      setSelectedContact(null);
      setIsNewContact(false);
      setSearchContact('');
      setShowContactDropdown(false);
      setTags([]);
      setTagInput('');
      setMessage(null);
      setDuplicateConfirmed(false);
      resetOutcomeFields();
    }
  }, [open, selectedPipelineId]);

  const handleContactSelect = (contact: any) => {
    const phoneNumber = getContactPhone(contact);

    console.log('📞 [AddOpportunity] Contato selecionado:', {
      id: contact.id,
      display_name: contact.display_name,
      phone: contact.phone,
      jid: contact.jid,
      phone_extracted: phoneNumber,
      push_name_last: contact.push_name_last
    });

    setSelectedContact(contact);
    setFormData(prev => ({
      ...prev,
      contactId: contact.id,
      newContactPhone: phoneNumber
    }));
    setSearchContact(contact.display_name || phoneNumber || '');
    setShowContactDropdown(false);
    setIsNewContact(false);
  };

  const handleNewContact = () => {
    setIsNewContact(true);
    setSelectedContact(null);
    setFormData(prev => ({
      ...prev,
      contactId: '',
      newContactPhone: '',
      newContactName: '',
      newContactCountryCode: DEFAULT_COUNTRY_CALLING_CODE,
    }));
    setShowContactDropdown(false);
  };

  const handleCancelNewContact = () => {
    setIsNewContact(false);
    setSearchContact('');
    setShowContactDropdown(false);
    setFormData(prev => ({
      ...prev,
      contactId: '',
      newContactName: '',
      newContactPhone: '',
      newContactCountryCode: DEFAULT_COUNTRY_CALLING_CODE,
    }));
    setMessage(prev => (prev?.type === 'error' ? null : prev));
  };

  const formatCurrency = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    const amount = parseFloat(numbers) / 100;
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numbers = value.replace(/\D/g, '');

    if (numbers === '') {
      setFormData(prev => ({ ...prev, price: '' }));
      return;
    }

    const formatted = formatCurrency(numbers);
    setFormData(prev => ({ ...prev, price: formatted }));
  };

  const getPriceValue = (): number => {
    if (!formData.price) return 0;
    const numbers = formData.price.replace(/\D/g, '');
    return parseFloat(numbers) / 100;
  };

  // As tags do card são guardadas normalizadas, igual às do contato: sem isso um
  // "VIP" digitado aqui não casaria com a tag "vip" da conta em nenhum filtro.
  const filteredTagSuggestions = accountTags
    .filter(t => t.is_active)
    .filter(t => !tags.includes(normalizeTagName(t.name)))
    .filter(t => !tagInput || t.name.toLowerCase().includes(tagInput.toLowerCase()));

  const addTagToCard = (tagName: string) => {
    const normalized = normalizeTagName(tagName);
    if (!normalized || tags.includes(normalized)) return;
    setTags([...tags, normalized]);
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTagToCard(tagInput);
      setTagInput('');
      setShowTagDropdown(false);
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      setTags(tags.slice(0, -1));
    } else if (e.key === 'Escape') {
      setShowTagDropdown(false);
    }
  };

  const handleSelectTag = (tagName: string) => {
    addTagToCard(tagName);
    setTagInput('');
    setShowTagDropdown(false);
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const pipelineToUse = formData.pipelineId || selectedPipelineId;
    const stageToUse = formData.stageId || firstStageId;

    if (!pipelineToUse || !stageToUse) {
      setMessage({ type: 'error', text: t('addOpportunity.errPipelineStage') });
      return;
    }

    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: t('addOpportunity.errNameRequired') });
      return;
    }

    if (isNewContact && (!formData.newContactName.trim() || !formData.newContactPhone.trim())) {
      setMessage({ type: 'error', text: t('addOpportunity.errContactFields') });
      return;
    }

    if (!isNewContact && !formData.contactId) {
      setMessage({ type: 'error', text: t('addOpportunity.errSelectContact') });
      return;
    }

    // Etapa de ganho/perda cria um card já fechado: sem estes dados ele entraria
    // na coluna sem contar como venda (nem como perda) em nenhum relatório
    if (derivedStatus === 'won' && !saleDate) {
      setMessage({ type: 'error', text: t('addOpportunity.saleDateRequired') });
      return;
    }

    if (saleDateIsFuture) {
      setMessage({ type: 'error', text: t('editOpportunity.saleDateFuture') });
      return;
    }

    if (missingLossReason) {
      setMessage({ type: 'error', text: t('editOpportunity.lossReasonMissing') });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      let contactIdToUse = formData.contactId;
      let contactNameToUse = selectedContact?.display_name || selectedContact?.push_name_last || '';

      if (isNewContact) {
        const fullPhone = joinPhoneWithCountryCode(
          formData.newContactCountryCode,
          formData.newContactPhone
        );
        const jid = `${fullPhone}@s.whatsapp.net`;

        const created = await createContact({
          jid,
          is_group: false,
          display_name: formData.newContactName,
          phone: fullPhone,
          push_name_last: formData.newContactName,
          countryCallingCode: formData.newContactCountryCode,
        } as any);

        if (!created.success) {
          setMessage({
            type: 'error',
            text: created.error || 'Erro ao criar contato',
          });
          return;
        }

        contactIdToUse = created.contact?.id || '';
        contactNameToUse = formData.newContactName;

        // Sem id não dá para vincular o card: criar assim geraria uma
        // oportunidade órfã, sem nome e sem link para o contato.
        if (!contactIdToUse) {
          setMessage({ type: 'error', text: t('addOpportunity.contactLinkFailed') });
          return;
        }
      }

      // Avisar (sem bloquear) quando o contato já tem card(s) neste funil
      if (contactIdToUse && !duplicateConfirmed) {
        const existing = await findExistingOpportunities(contactIdToUse, pipelineToUse);
        if (existing.length > 0) {
          setMessage({
            type: 'warning',
            text: t('duplicateWarning.body', {
              count: existing.length,
              titles: existing.map((o: { title: string }) => o.title).join(', '),
            }),
          });
          setDuplicateConfirmed(true);
          setLoading(false);
          return;
        }
      }

      const closedAt =
        derivedStatus === 'won'
          ? dateInputToIso(saleDate) || new Date().toISOString()
          : derivedStatus === 'lost'
            ? new Date().toISOString()
            : undefined;

      const opportunity = await createOpportunity({
        pipeline_id: pipelineToUse,
        stage_id: stageToUse,
        contact_id: contactIdToUse || undefined,
        title: `${formData.title} (${contactNameToUse})`,
        product: formData.product || undefined,
        price: getPriceValue(),
        tags: tags,
        status: derivedStatus,
        closed_at: closedAt,
        sale_date: derivedStatus === 'won' ? dateInputToIso(saleDate) : undefined,
        won_by_user_id: derivedStatus === 'won' && wonByUserId ? wonByUserId : undefined,
        loss_reason_id: derivedStatus === 'lost' && lossReasonId ? lossReasonId : undefined,
        lost_by_user_id: derivedStatus === 'lost' && lostByUserId ? lostByUserId : undefined,
      });

      if (opportunity) {
        // Close dialog immediately
        onOpenChange(false);

        // Reset form
        setFormData({
          contactId: '',
          newContactName: '',
          newContactPhone: '',
          newContactCountryCode: DEFAULT_COUNTRY_CALLING_CODE,
          title: '',
          product: '',
          price: '',
          pipelineId: selectedPipelineId,
          stageId: ''
        });
        setTags([]);
        setSearchContact('');
        resetOutcomeFields();

        // Switch pipeline if needed (only when creating in different pipeline)
        if (onPipelineChange && pipelineToUse !== selectedPipelineId) {
          setTimeout(() => {
            onPipelineChange(pipelineToUse);
          }, 100);
        }
      } else {
        setMessage({ type: 'error', text: t('addOpportunity.errCreate') });
      }
    } catch (error) {
      console.error('Erro ao criar oportunidade:', error);
      setMessage({ type: 'error', text: t('addOpportunity.errCreate') });
    } finally {
      setLoading(false);
    }
  };

  // Debug: Log estado atual
  console.log('🔍 [AddOpportunity] Estado atual:', {
    isNewContact,
    selectedContact: selectedContact ? {
      id: selectedContact.id,
      phone: selectedContact.phone,
      display_name: selectedContact.display_name
    } : null,
    formDataPhone: formData.newContactPhone
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex !max-w-[600px] rounded-2xl w-full p-6 border-border bg-card flex-col max-h-[90vh] overflow-y-auto dark:border-[#3F3F464D] dark:bg-[#18181B]">
        <DialogHeader>
          <DialogTitle className="text-[20px] text-foreground dark:text-neutral-50 font-semibold">
            {t("addOpportunity.title")}
          </DialogTitle>
        </DialogHeader>

        {message && (
          <div className={`p-3 rounded-md mb-2 ${
            message.type === 'success'
              ? 'bg-green-500/20 border border-green-500/30 text-green-300'
              : message.type === 'warning'
                ? 'bg-amber-500/20 border border-amber-500/30 text-amber-300'
                : 'bg-red-500/20 border border-red-500/30 text-red-300'
          }`}>
            {message.type === 'warning' && (
              <p className="font-semibold mb-1">{t('duplicateWarning.title')}</p>
            )}
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
          {/* Em "Novo Contato" a busca sai de cena: quem está criando um contato não
              tem o que procurar, e o campo aberto só confunde */}
          {!isNewContact && (
            <div className="flex flex-col gap-2 relative">
              <Label htmlFor="contact" className="text-foreground dark:text-neutral-50">{t("addOpportunity.contactRequired")}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
                <Input
                  id="contact"
                  value={searchContact}
                  onChange={(e) => {
                    setSearchContact(e.target.value);
                    setShowContactDropdown(true);
                  }}
                  onFocus={() => setShowContactDropdown(true)}
                  placeholder={t("addOpportunity.searchContact")}
                  className="bg-white border-border text-foreground pl-10 dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
                  autoComplete="off"
                />
              </div>

              {showContactDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-xl max-h-[200px] overflow-y-auto z-50 shadow-md dark:bg-[#27272A] dark:border-[#3F3F464D] dark:rounded-md dark:shadow-lg">
                  <button
                    type="button"
                    onClick={handleNewContact}
                    className="w-full text-left px-4 py-2 hover:bg-[#F1F5F9] text-foreground border-b border-border flex items-center gap-2 dark:hover:bg-[#3F3F46] dark:text-neutral-50 dark:border-[#3F3F464D]"
                  >
                    <span className="text-green-400">+</span> {t("addOpportunity.newContact")}
                  </button>
                  {filteredContacts.length === 0 ? (
                    <div className="px-4 py-3 text-neutral-400 text-sm">
                      {t("addOpportunity.noContact")}
                    </div>
                  ) : (
                    filteredContacts.map(contact => (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => handleContactSelect(contact)}
                        className="w-full text-left px-4 py-2 hover:bg-[#F1F5F9] text-foreground border-b border-border last:border-b-0 dark:hover:bg-[#3F3F46] dark:text-neutral-50 dark:border-[#3F3F464D]"
                      >
                        <div className="font-medium">{contact.display_name || contact.phone}</div>
                        {contact.display_name && contact.phone && (
                          <div className="text-xs text-neutral-400">{contact.phone}</div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {isNewContact && (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground dark:text-neutral-50">
                  {t("addOpportunity.newContact")}
                </span>
                <button
                  type="button"
                  onClick={handleCancelNewContact}
                  className="text-xs text-[#2563EB] hover:underline dark:text-[#60A5FA]"
                >
                  {t("addOpportunity.backToSearch")}
                </button>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="newContactName" className="text-foreground dark:text-neutral-50">{t("addOpportunity.contactNameRequired")}</Label>
                <Input
                  id="newContactName"
                  value={formData.newContactName}
                  onChange={(e) => setFormData(prev => ({ ...prev, newContactName: e.target.value }))}
                  placeholder={t("addOpportunity.contactNamePlaceholder")}
                  className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="newContactPhone" className="text-foreground dark:text-neutral-50">{t("common.phoneRequired")}</Label>
                <div className="flex gap-2 w-full">
                  <Select
                    value={formData.newContactCountryCode}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, newContactCountryCode: value }))
                    }
                  >
                    <SelectTrigger className="bg-white border-border text-foreground w-[168px] shrink-0 dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[280px] bg-card border-border dark:bg-[#27272A] dark:border-[#3F3F464D]">
                      <SelectGroup>
                        {COUNTRY_CALLING_CODES.map((country) => (
                          <SelectItem
                            key={`${country.code}-${country.name}`}
                            value={country.code}
                            className="text-foreground dark:text-neutral-50"
                          >
                            <span>
                              {country.flag} {getCountryCallingCodeLabel(country)}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Input
                    id="newContactPhone"
                    value={formData.newContactPhone}
                    onChange={(e) => {
                      const onlyNumbers = e.target.value.replace(/\D/g, '');
                      setFormData((prev) => ({ ...prev, newContactPhone: onlyNumbers }));
                    }}
                    placeholder="11999999999"
                    className="bg-white border-border text-foreground flex-1 dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
                    type="tel"
                    inputMode="numeric"
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground dark:text-neutral-400">
                  {t("addOpportunity.phoneHint")}
                </p>
              </div>
            </>
          )}

          {!isNewContact && formData.contactId && (
            <div className="flex flex-col gap-2">
              <Label className="text-foreground dark:text-neutral-50">{t("common.phone")}</Label>
              <Input
                value={formData.newContactPhone || 'Não informado'}
                disabled
                className="bg-[#F8FAFC] border-border text-muted-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-400"
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="pipeline" className="text-foreground dark:text-neutral-50">{t("common.pipelineRequired")}</Label>
            <Select
              value={formData.pipelineId}
              onValueChange={(value) => setFormData(prev => ({ ...prev, pipelineId: value, stageId: '' }))}
            >
              <SelectTrigger className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50">
                <SelectValue placeholder={t("addOpportunity.pickPipeline")} />
              </SelectTrigger>
              <SelectContent className="bg-card border-border dark:bg-[#27272A] dark:border-[#3F3F464D]">
                {pipelines.filter(p => p.is_active !== false).map((pipeline) => (
                  <SelectItem key={pipeline.id} value={pipeline.id} className="text-foreground dark:text-neutral-50">
                    {pipeline.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="stage" className="text-foreground dark:text-neutral-50">{t("common.stageRequired")}</Label>
            <Select
              value={formData.stageId}
              onValueChange={applyStage}
              disabled={!formData.pipelineId}
            >
              <SelectTrigger className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50">
                <SelectValue placeholder={t("addOpportunity.pickStage")} />
              </SelectTrigger>
              <SelectContent className="bg-card border-border dark:bg-[#27272A] dark:border-[#3F3F464D]">
                {pipelineStages.map((stage) => (
                  <SelectItem key={stage.id} value={stage.id} className="text-foreground dark:text-neutral-50">
                    {stage.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {derivedStatus === 'won' && (
            <>
              <div className="flex flex-col gap-2">
                <Label htmlFor="saleDate" className="text-foreground dark:text-neutral-50">
                  {t("editOpportunity.saleDate")} *
                </Label>
                <Input
                  id="saleDate"
                  type="date"
                  value={saleDate}
                  max={today}
                  onChange={(e) => setSaleDate(e.target.value)}
                  aria-invalid={saleDateIsFuture}
                  className="bg-white border-border text-foreground [color-scheme:light] dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50 dark:[color-scheme:dark]"
                />
                {saleDateIsFuture && (
                  <p className="text-xs text-red-500 dark:text-red-400">
                    {t("editOpportunity.saleDateFuture")}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label className="text-foreground dark:text-neutral-50">{t("editOpportunity.seller")}</Label>
                <Select
                  value={wonByUserId || 'none'}
                  onValueChange={(v) => setWonByUserId(v === 'none' ? '' : v)}
                  disabled={loadingMembers}
                >
                  <SelectTrigger className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50">
                    <SelectValue placeholder={loadingMembers ? 'Carregando...' : 'Selecione'} />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border dark:bg-[#27272A] dark:border-[#3F3F464D]">
                    <SelectGroup>
                      <SelectItem value="none" className="text-foreground dark:text-neutral-50">
                        {t("common.none")}
                      </SelectItem>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id} className="text-foreground dark:text-neutral-50">
                          {member.name || member.email}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {derivedStatus === 'lost' && (
            <>
              <div className="flex flex-col gap-2">
                <Label className="text-foreground dark:text-neutral-50">
                  {t("editOpportunity.lossReasonRequired")}
                </Label>
                <Select
                  value={lossReasonId}
                  onValueChange={(value) => {
                    if (value === '__new__') {
                      setIsAddingReason(true);
                      setLossReasonId('');
                    } else {
                      setLossReasonId(value);
                      setIsAddingReason(false);
                    }
                  }}
                  disabled={loadingReasons}
                >
                  <SelectTrigger
                    className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
                    aria-invalid={missingLossReason}
                  >
                    <SelectValue placeholder={loadingReasons ? 'Carregando...' : t("loss.pickReason")} />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border dark:bg-[#27272A] dark:border-[#3F3F464D]">
                    <SelectGroup>
                      {lossReasons.map((reason) => (
                        <SelectItem key={reason.id} value={reason.id} className="text-foreground dark:text-neutral-50">
                          {reason.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__new__" className="text-[#2563EB] dark:text-blue-400">
                        <span className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          {t("loss.addReason")}
                        </span>
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              {isAddingReason && (
                <div className="flex flex-col gap-2 rounded-xl border border-border bg-[#F8FAFC] p-3 dark:border-[#3F3F464D] dark:bg-[#18181B]">
                  <Label className="text-foreground dark:text-neutral-50">{t("loss.newReason")}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newReasonName}
                      onChange={(e) => setNewReasonName(e.target.value)}
                      placeholder={t("loss.newReasonPlaceholder")}
                      className="flex-1 bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleCreateNewReason();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={() => void handleCreateNewReason()}
                      disabled={!newReasonName.trim() || isCreatingReason}
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                    >
                      {isCreatingReason ? '...' : t("common:actions.save")}
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Label className="text-foreground dark:text-neutral-50">{t("editOpportunity.lossOwner")}</Label>
                <Select
                  value={lostByUserId || 'none'}
                  onValueChange={(v) => setLostByUserId(v === 'none' ? '' : v)}
                  disabled={loadingMembers}
                >
                  <SelectTrigger className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50">
                    <SelectValue placeholder={loadingMembers ? 'Carregando...' : 'Selecione'} />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border dark:bg-[#27272A] dark:border-[#3F3F464D]">
                    <SelectGroup>
                      <SelectItem value="none" className="text-foreground dark:text-neutral-50">
                        {t("common.none")}
                      </SelectItem>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id} className="text-foreground dark:text-neutral-50">
                          {member.name || member.email}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor="title" className="text-foreground dark:text-neutral-50">{t("addOpportunity.opportunityNameRequired")}</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder={t("addOpportunity.opportunityNamePlaceholder")}
              className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="product" className="text-foreground dark:text-neutral-50">{t("addOpportunity.product")}</Label>
            <Input
              id="product"
              value={formData.product}
              onChange={(e) => setFormData(prev => ({ ...prev, product: e.target.value }))}
              placeholder={t("addOpportunity.productPlaceholder")}
              className="bg-white border-border text-foreground dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="price" className="text-foreground dark:text-neutral-50">{t("addOpportunity.value")}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">{symbol}</span>
              <Input
                id="price"
                type="text"
                value={formData.price}
                onChange={handlePriceChange}
                placeholder="0,00"
                className="bg-white border-border text-foreground pl-12 dark:bg-[#27272A] dark:border-[#3F3F464D] dark:text-neutral-50"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 relative" ref={tagContainerRef}>
            <Label htmlFor="tags" className="text-foreground dark:text-neutral-50">{t("common.tags")}</Label>
            <div className="flex flex-wrap gap-2 p-2 bg-white border border-border rounded-xl min-h-[42px] dark:bg-[#27272A] dark:border-[#3F3F464D] dark:rounded-md">
              {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm bg-[#DBEAFE] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="hover:text-red-400 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </span>
              ))}
              <Input
                id="tags"
                value={tagInput}
                onChange={(e) => {
                  setTagInput(e.target.value);
                  setShowTagDropdown(true);
                }}
                onFocus={() => setShowTagDropdown(true)}
                onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                onKeyDown={handleTagKeyDown}
                placeholder={tags.length === 0 ? t("addOpportunity.tagSearchPlaceholder") : ""}
                className="flex-1 min-w-[120px] border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground dark:text-neutral-50 p-0"
              />
            </div>
            {showTagDropdown && filteredTagSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-xl max-h-[200px] overflow-y-auto z-50 shadow-md dark:bg-[#27272A] dark:border-[#3F3F464D] dark:rounded-md dark:shadow-lg">
                {filteredTagSuggestions.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelectTag(tag.name)}
                    className="w-full text-left px-4 py-2 text-foreground hover:bg-[#F1F5F9] border-b border-border last:border-b-0 flex items-center gap-2 transition-colors dark:text-neutral-50 dark:hover:bg-[#3F3F46] dark:border-[#3F3F464D]"
                  >
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                  </button>
                ))}
              </div>
            )}
            <p className="text-xs text-neutral-400">{t("addOpportunity.tagsHint")}</p>
          </div>

          <div className="flex gap-3 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-border bg-transparent hover:bg-[#F1F5F9] text-foreground dark:border-[#3F3F464D] dark:hover:bg-[#27272A] dark:text-neutral-50"
              disabled={loading}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              disabled={loading}
            >
              {loading
                ? 'Criando...'
                : duplicateConfirmed
                  ? t('duplicateWarning.confirm')
                  : t('addOpportunity.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddOpportunityDialog;
